import { trackedEntity } from '@cineamo/pulse/src/subject';
import type { PulseEvent } from '@cineamo/pulse/src/types';

import type { AdvertisementTrackEvent, AdvertisementTrackPayload } from './types';

/**
 * Maps an `onTrack` callback into the Pulse event to send for it (CIN-14644).
 *
 * Lives here rather than in each host because every host sends the *same* event — the mapping is a
 * property of what an ad interaction means, not of which app served it. `apps/web` and
 * cinema-website had identical copies of this before it was extracted, which is exactly how the two
 * drift apart and a report ends up depending on which app the ad was shown in.
 *
 * The subject itself is resolved by `trackedEntity()`, so this does not re-decide the
 * content-vs-movie question that ADR 0001 deliberately centralised there ("the choice lives here
 * rather than being re-decided per component"). For a film ad that yields the `content` entity, which
 * `InterestTrackHandler` resolves to the movie server-side (CIN-14405) — filling the generated
 * `interest_event.movie_id` and so making the event visible to the distributor queries and the four
 * aggregate tables.
 *
 * The one thing this adds on top is the no-subject case, which `trackedEntity()` cannot express
 * (`contentId` is required there — "passing neither is not representable" — because cinema-website
 * programming always has one). An advertisement genuinely can have no subject: external ads
 * (`adCategory !== 'film'`) are not about a film at all, so they are sent entity-less rather than
 * with an `entity_type` and no `entity_id`, which would store an event pointing at nothing.
 *
 * See `docs/adr/0001-stop-recording-interest-in-non-film-content.md`.
 */
export function advertisementPulseEvent(
    event: AdvertisementTrackEvent,
    payload: AdvertisementTrackPayload
): PulseEvent {
    return {
        event_type: event,
        ...(payload.contentId !== undefined ? trackedEntity({ contentId: payload.contentId }) : {}),
        context: {
            advertisement_id: payload.advertisementId,
            // The served A/B variant — the Version level of the `Movie › Ad › Version` tree.
            variant_id: payload.variantId,
            placement: payload.placement
        }
    };
}
